import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import ChampDeTexte from '../../UI/ChampDeTexte/ChampDeTexte';
import DateRangeInput from '../TypesTasks/DateRangeInput';
import DateInput from '../TypesTasks/DateInput.tsx';
import NumberInput from '../TypesTasks/NumberInput';
import SelectInput from '../TypesTasks/SelectInput';
import SensorsListInput from '../TypesTasks/SensorsListInput.tsx';
import TextInput from '../TypesTasks/TextInput';

/**
 * Composant unifié pour afficher un champ de formulaire selon le type d'argument
 * Gère différents types d'inputs (capteurs, dates, nombres, texte, etc.)
 *
 * @param {Object} argument - L'argument à afficher
 * @param {number} index - L'index de l'argument dans la liste
 * @param {Function} onChange - Callback appelé lors du changement de valeur
 * @param {string} projectId - ID du projet pour filtrer les capteurs
 * @param {boolean} readOnly - Si true, désactiver les inputs
 */
const ArgumentInput = ({
  argument,
  index,
  onChange,
  projectId,
  readOnly = false,
}) => {
  const handleChange = useCallback(
    (value) => {
      onChange(index, 'valeurParDefaut', value);
    },
    [index, onChange]
  );

  const handleComplexChange = useCallback(
    (field, value) => {
      onChange(index, field, value);
    },
    [index, onChange]
  );

  const currentValue = argument.valeurParDefaut || argument.value || '';

  // Mapping unifié des types vers leurs composants
  const renderByType = () => {
    switch (argument.type) {
      case 'SensorList':
      case 'DevuiList':
        return (
          <SensorsListInput
            argument={argument}
            onChange={handleComplexChange}
            projectId={projectId}
            disabled={readOnly}
          />
        );
      case 'Sensor':
        return (
          <SensorsListInput
            argument={argument}
            onChange={handleComplexChange}
            selectionMode={'single'}
            projectId={projectId}
            disabled={readOnly}
          />
        );
      case 'DateRange':
        return (
          <DateRangeInput
            argument={argument}
            onChange={handleComplexChange}
            disabled={readOnly}
          />
        );

      case 'DateOnly':
      case 'DateTime':
        return (
          <DateInput
            argument={argument}
            onChange={handleComplexChange}
            disabled={readOnly}
          />
        );

      case 'Number':
        return (
          <NumberInput
            argument={argument}
            onChange={handleComplexChange}
            disabled={readOnly}
          />
        );

      case 'String':
        return (
          <TextInput
            argument={argument}
            onChange={handleComplexChange}
            disabled={readOnly}
          />
        );

      case 'Mode':
        return (
          <SelectInput
            argument={argument}
            onChange={handleComplexChange}
            disabled={readOnly}
          />
        );

      default:
        // Champ texte par défaut
        return (
          <ChampDeTexte
            value={currentValue}
            onChange={handleChange}
            placeholder={`Valeur pour ${argument.nom}`}
            disabled={readOnly}
          />
        );
    }
  };

  return renderByType();
};

ArgumentInput.propTypes = {
  argument: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nom: PropTypes.string,
    type: PropTypes.string.isRequired,
    obligatoire: PropTypes.bool,
    description: PropTypes.string,
    valeurParDefaut: PropTypes.any,
    value: PropTypes.any,
    options: PropTypes.array,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
};

ArgumentInput.defaultProps = {
  projectId: null,
  readOnly: false,
};

export default React.memo(ArgumentInput);
